import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listFlows, type FlowSummary } from '../api/flows.js';
import { listChannels, type Channel, type ChannelType } from '../api/channels.js';
import { useTenantSlug } from '../hooks/useTenantSlug.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const CHANNEL_ICON: Record<ChannelType, string> = {
  webchat: '💬',
  whatsapp: '🟢',
  messenger: '🔵',
  instagram: '📷',
  five9: '☎️',
};

const CHANNEL_LABEL: Record<ChannelType, string> = {
  webchat: 'Webchat',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  five9: 'Five9',
};

function fmtVersion(v: string | undefined): string {
  if (!v) return '—';
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return `Publicado ${new Date(v).toLocaleString()}`;
    } catch {
      return v;
    }
  }
  return `v${v}`;
}

interface ChannelGroup {
  channelType: ChannelType;
  channels: Channel[];
  flow: FlowSummary | null;
}

export function FlowList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const tenantSlug = useTenantSlug(id);

  const channelsQuery = useQuery({
    queryKey: ['channels', id, { includeArchived: false }],
    queryFn: () => listChannels(id),
    enabled: Boolean(id),
  });

  const flowsQuery = useQuery({
    queryKey: ['flows', tenantSlug],
    queryFn: () => listFlows(tenantSlug),
    enabled: Boolean(tenantSlug),
    retry: false,
  });

  const isLoading = channelsQuery.isLoading || flowsQuery.isLoading;
  const error = channelsQuery.error || flowsQuery.error;
  const isFetching = channelsQuery.isFetching || flowsQuery.isFetching;

  // Agrupamos channels por tipo y matcheamos al flow correspondiente.
  const groups: ChannelGroup[] = useMemo(() => {
    const channels = (channelsQuery.data ?? []).filter((c) => c.deletedAt === null);
    const flows = flowsQuery.data ?? [];
    const byType = new Map<ChannelType, Channel[]>();
    for (const c of channels) {
      const arr = byType.get(c.channelType) ?? [];
      arr.push(c);
      byType.set(c.channelType, arr);
    }
    return Array.from(byType.entries()).map(([channelType, list]) => ({
      channelType,
      channels: list,
      flow: flows.find((f) => f.channel === channelType) ?? null,
    }));
  }, [channelsQuery.data, flowsQuery.data]);

  function refetchAll(): void {
    void channelsQuery.refetch();
    void flowsQuery.refetch();
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t.flow.listTitle}</h2>
          <p className="mt-1 text-xs text-slate-500">{t.flow.intro}</p>
        </div>
        <button
          type="button"
          onClick={refetchAll}
          disabled={isFetching}
          className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100 disabled:opacity-50"
        >
          {isFetching ? '…' : t.flow.refresh}
        </button>
      </div>

      {isLoading && <p>{t.app.loading}</p>}
      {error && (
        <p className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800">
          {(error as Error).message}
        </p>
      )}

      {!isLoading && !error && groups.length === 0 && (
        <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-sm">
          <p className="font-medium text-slate-700">{t.flow.noChannelsTitle}</p>
          <p className="mt-1 text-slate-500">{t.flow.noChannelsDesc}</p>
          <Link
            to={`/tenants/${id}/channels`}
            className="mt-3 inline-block rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.flow.goToChannels}
          </Link>
        </div>
      )}

      {groups.map((g) => (
        <ChannelGroupCard key={g.channelType} group={g} tenantPathId={id} />
      ))}
    </div>
  );
}

function ChannelGroupCard({
  group,
  tenantPathId,
}: {
  group: ChannelGroup;
  tenantPathId: string;
}): JSX.Element {
  const hasFlow = group.flow !== null;
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
        <span aria-hidden className="text-2xl">
          {CHANNEL_ICON[group.channelType]}
        </span>
        <div className="flex-1">
          <div className="font-medium">{CHANNEL_LABEL[group.channelType]}</div>
          <div className="text-xs text-slate-500">
            {group.channels.length} {group.channels.length === 1 ? 'canal' : 'canales'}
            {' · '}
            {hasFlow ? fmtVersion(group.flow!.version) : t.flow.notDeployed}
          </div>
        </div>
        <Link
          to={`/tenants/${tenantPathId}/flows/${group.channelType}`}
          className={`rounded px-3 py-1 text-sm ${
            hasFlow
              ? 'border border-slate-300 hover:bg-slate-100'
              : 'bg-brand-500 text-white hover:bg-brand-600'
          }`}
        >
          {hasFlow ? t.flow.edit : t.flow.create}
        </Link>
      </header>
      <ul className="divide-y divide-slate-100 text-sm">
        {group.channels.map((c) => (
          <li key={c.id} className="flex items-center gap-3 px-4 py-2">
            <span
              className={`h-2 w-2 rounded-full ${
                c.status === 'connected'
                  ? 'bg-emerald-500'
                  : c.status === 'error'
                    ? 'bg-red-500'
                    : 'bg-slate-300'
              }`}
              aria-hidden
            />
            <span className="flex-1">
              <span className="font-medium">{c.name}</span>
              {c.displayPhoneNumber && (
                <span className="ml-2 text-xs text-slate-500">{c.displayPhoneNumber}</span>
              )}
              {c.domain && <span className="ml-2 text-xs text-slate-500">{c.domain}</span>}
            </span>
            <span className="text-xs text-slate-400">{c.status}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
